import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TrendingDown, TrendingUp, Minus, Plus, ExternalLink, Search, Wallet, X } from 'lucide-react'
import { Layout } from '@/components/Layout'
import { supabase } from '@/services/SupabaseClient/supabase-client'
import { targetAssetsService } from '@/services'
import type { FiiTrend } from '@/types/FiiAnalysis'
import './fii-radar.css'

const TREND_CONFIG: Record<FiiTrend, { label: string; icon: typeof TrendingUp; className: string }> = {
  growing:  { label: 'Crescendo', icon: TrendingUp,  className: 'fii-radar__badge--growing' },
  stable:   { label: 'Estável',   icon: Minus,        className: 'fii-radar__badge--stable' },
  declining:{ label: 'Em queda',  icon: TrendingDown, className: 'fii-radar__badge--declining' },
}

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
}

function formatMonth(isoDate: string): string {
  const [year, month] = isoDate.split('-')
  return `${MONTH_LABELS[month] ?? month}/${year.slice(2)}`
}

interface FiiRadarCard {
  cnpj: string
  ticker: string
  name: string | null
  segment: string | null
  competence_month: string
  health_score: number
  trend: FiiTrend
  positive_points: string[]
  negative_points: string[]
}

async function fetchFiiAnalyses(): Promise<FiiRadarCard[]> {
  // Views `latest_*` (scripts/radar_views.sql) já retornam uma linha por ativo.
  const { data: analyses } = await supabase
    .from('latest_asset_ai_analyses')
    .select('cnpj, ticker, health_score, trend, positive_points, negative_points, competence_month')
    .eq('asset_type', 'FII')
    .order('competence_month', { ascending: false })

  if (!analyses?.length) return []

  const [{ data: assets }, { data: monthly }] = await Promise.all([
    supabase.from('assets').select('ticker, long_name, short_name').eq('type', 'FII'),
    supabase.from('latest_fii_monthly_data').select('cnpj, segment'),
  ])

  const assetByTicker = new Map((assets ?? []).map((a) => [a.ticker, a]))
  const segmentByCnpj = new Map<string, string>()
  for (const row of monthly ?? []) {
    if (row.segment) segmentByCnpj.set(row.cnpj, row.segment)
  }

  return analyses.map((a) => {
    const asset = assetByTicker.get(a.ticker)
    return {
      cnpj: a.cnpj,
      ticker: a.ticker,
      name: asset?.short_name || asset?.long_name || null,
      segment: segmentByCnpj.get(a.cnpj) ?? null,
      competence_month: a.competence_month,
      health_score: a.health_score,
      trend: a.trend as FiiTrend,
      positive_points: a.positive_points ?? [],
      negative_points: a.negative_points ?? [],
    }
  })
}

export function FiiRadar() {
  const { data, isLoading } = useQuery({
    queryKey: ['fii-radar'],
    queryFn: fetchFiiAnalyses,
    staleTime: 1000 * 60 * 5,
  })

  const fiis = data ?? []

  const { data: portfolioAssets } = useQuery({
    queryKey: ['target-assets'],
    queryFn: targetAssetsService.list,
    staleTime: 1000 * 60 * 5,
  })

  const portfolioTickers = useMemo(() => {
    const set = new Set<string>()
    for (const asset of portfolioAssets ?? []) {
      if (asset.quantity > 0) set.add(asset.ticker)
    }
    return set
  }, [portfolioAssets])

  const [search, setSearch] = useState('')
  const [segmentFilter, setSegmentFilter] = useState('')
  const [trendFilter, setTrendFilter] = useState<FiiTrend | ''>('')
  const [onlyPortfolio, setOnlyPortfolio] = useState(false)

  const segments = useMemo(() => {
    const set = new Set<string>()
    for (const fii of fiis) {
      if (fii.segment) set.add(fii.segment)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [fiis])

  const hasActiveFilters = search.trim() !== '' || segmentFilter !== '' || trendFilter !== '' || onlyPortfolio

  const filteredFiis = useMemo(() => {
    const term = search.trim().toLowerCase()
    return fiis.filter((fii) => {
      if (term) {
        const matchesTerm =
          fii.ticker.toLowerCase().includes(term) || (fii.name?.toLowerCase().includes(term) ?? false)
        if (!matchesTerm) return false
      }
      if (segmentFilter && fii.segment !== segmentFilter) return false
      if (trendFilter && fii.trend !== trendFilter) return false
      if (onlyPortfolio && !portfolioTickers.has(fii.ticker)) return false
      return true
    })
  }, [fiis, search, segmentFilter, trendFilter, onlyPortfolio, portfolioTickers])

  const clearFilters = () => {
    setSearch('')
    setSegmentFilter('')
    setTrendFilter('')
    setOnlyPortfolio(false)
  }

  return (
    <Layout>
      <section className="fii-radar app-page">
        <header className="fii-radar__header">
          <div>
            <h1>FII Radar</h1>
            <p>Análises geradas por IA com dados mensais da CVM</p>
          </div>
        </header>

        {!isLoading && fiis.length > 0 && (
          <div className="fii-radar__filters">
            <div className="fii-radar__search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Buscar por ticker ou nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select value={segmentFilter} onChange={(e) => setSegmentFilter(e.target.value)}>
              <option value="">Todos os segmentos</option>
              {segments.map((segment) => (
                <option key={segment} value={segment}>
                  {segment}
                </option>
              ))}
            </select>

            <select value={trendFilter} onChange={(e) => setTrendFilter(e.target.value as FiiTrend | '')}>
              <option value="">Todas as tendências</option>
              <option value="growing">Crescendo</option>
              <option value="stable">Estável</option>
              <option value="declining">Em queda</option>
            </select>

            <button
              type="button"
              className="fii-radar__portfolio-toggle"
              data-active={onlyPortfolio}
              onClick={() => setOnlyPortfolio((prev) => !prev)}
            >
              <Wallet size={14} />
              Só minha carteira
            </button>

            {hasActiveFilters && (
              <button type="button" className="fii-radar__clear-filters" onClick={clearFilters}>
                <X size={14} />
                Limpar
              </button>
            )}
          </div>
        )}

        {isLoading && (
          <div className="app-state app-state--loading">
            <p>Carregando análises...</p>
          </div>
        )}

        {!isLoading && fiis.length === 0 && (
          <div className="app-state app-state--empty">
            <p>Nenhuma análise disponível ainda.</p>
          </div>
        )}

        {!isLoading && fiis.length > 0 && filteredFiis.length === 0 && (
          <div className="app-state app-state--empty">
            <p>Nenhum resultado para os filtros aplicados.</p>
          </div>
        )}

        {!isLoading && filteredFiis.length > 0 && (
          <div className="fii-radar__grid">
            {filteredFiis.map((fii) => {
              const trendConfig = TREND_CONFIG[fii.trend]
              const TrendIcon = trendConfig?.icon

              return (
                <Link
                  key={fii.cnpj}
                  to={`/market/${fii.ticker}`}
                  className="fii-radar__card"
                >
                  <div className="fii-radar__card-top">
                    <div className="fii-radar__card-heading">
                      <span className="fii-radar__ticker">{fii.ticker}</span>
                      {fii.name && <span className="fii-radar__name">{fii.name}</span>}
                      <span className="fii-radar__month">{formatMonth(fii.competence_month)}</span>
                    </div>
                    <span className="fii-radar__link-icon">
                      <ExternalLink size={14} />
                    </span>
                  </div>

                  <div className="fii-radar__score-row">
                    <span
                      className="fii-radar__score"
                      data-score={
                        fii.health_score >= 7 ? 'high' : fii.health_score >= 4 ? 'mid' : 'low'
                      }
                    >
                      <strong>{fii.health_score}</strong>
                      <small>/10</small>
                    </span>
                    {trendConfig && TrendIcon && (
                      <span className={`fii-radar__badge ${trendConfig.className}`}>
                        <TrendIcon size={12} />
                        {trendConfig.label}
                      </span>
                    )}
                  </div>

                  {(fii.positive_points[0] || fii.negative_points[0]) && (
                    <div className="fii-radar__highlights">
                      {fii.positive_points[0] && (
                        <p className="fii-radar__highlight fii-radar__highlight--positive">
                          <Plus size={12} />
                          <span>{fii.positive_points[0]}</span>
                        </p>
                      )}
                      {fii.negative_points[0] && (
                        <p className="fii-radar__highlight fii-radar__highlight--negative">
                          <Minus size={12} />
                          <span>{fii.negative_points[0]}</span>
                        </p>
                      )}
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </section>
    </Layout>
  )
}
